/**
 * Password change form component with 3-step validation.
 */
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../../auth/hooks/useAuth';
import { passwordSchema } from '../../auth/validationSchema';
import * as settingsApi from '../services/settingsApi';
import { supabase } from '../../../shared/lib/supabaseClient';

const passwordChangeSchema = z.object({
  current_password: z.string().min(1, 'Current password is required'),
  new_password: passwordSchema,
  confirm_password: z.string().min(1, 'Please confirm your new password'),
}).refine((data) => data.new_password === data.confirm_password, {
  message: "Passwords don't match",
  path: ["confirm_password"],
});

type PasswordChangeFormData = z.infer<typeof passwordChangeSchema>;

const PasswordChangeForm: React.FC = () => {
  const { session, user } = useAuth();
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<PasswordChangeFormData>({
    resolver: zodResolver(passwordChangeSchema),
  });

  const onSubmit = async (data: PasswordChangeFormData) => {
    if (!session?.access_token) {
      setError('Not authenticated');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      setSuccess(false);

      await settingsApi.changePassword(session.access_token, {
        current_password: data.current_password,
        new_password: data.new_password,
      });

      // Password change invalidates the current session
      // Refresh session by signing in again with the new password
      // This keeps the user logged in seamlessly
      if (!user?.email) {
        throw new Error('User email not found');
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: data.new_password,
      });

      if (signInError) {
        throw new Error(`Failed to refresh session: ${signInError.message}`);
      }

      setSuccess(true);
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {success && (
        <div className="rounded-md bg-green-50 p-4">
          <div className="text-sm text-green-700">Password changed successfully!</div>
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      <div>
        <label htmlFor="current_password" className="block text-sm font-medium text-gray-700">
          Current Password
        </label>
        <div className="mt-1">
          <input
            id="current_password"
            type="password"
            autoComplete="current-password"
            className={`appearance-none block w-full px-3 py-2 border ${
              errors.current_password ? 'border-red-300' : 'border-gray-300'
            } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
            {...register('current_password')}
          />
          {errors.current_password && (
            <p className="mt-2 text-sm text-red-600">{errors.current_password.message}</p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="new_password" className="block text-sm font-medium text-gray-700">
          New Password
        </label>
        <div className="mt-1">
          <input
            id="new_password"
            type="password"
            autoComplete="new-password"
            className={`appearance-none block w-full px-3 py-2 border ${
              errors.new_password ? 'border-red-300' : 'border-gray-300'
            } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
            {...register('new_password')}
          />
          {errors.new_password && (
            <p className="mt-2 text-sm text-red-600">{errors.new_password.message}</p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700">
          Confirm New Password
        </label>
        <div className="mt-1">
          <input
            id="confirm_password"
            type="password"
            autoComplete="new-password"
            className={`appearance-none block w-full px-3 py-2 border ${
              errors.confirm_password ? 'border-red-300' : 'border-gray-300'
            } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
            {...register('confirm_password')}
          />
          {errors.confirm_password && (
            <p className="mt-2 text-sm text-red-600">{errors.confirm_password.message}</p>
          )}
        </div>
      </div>

      <div>
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Changing Password...' : 'Change Password'}
        </button>
      </div>
    </form>
  );
};

export default PasswordChangeForm;

